<template>
    <b-card no-body class="h-100 mb-0 category-card" :class="`category-card--${variant}`">

        <!-- Header -->
        <div class="category-card__header" :class="`bg-light-${variant}`">
            <div class="d-flex align-items-center">
                <b-avatar rounded :variant="`light-${variant}`" size="38" class="mr-1">
                    <feather-icon :icon="icon" size="20" />
                </b-avatar>
                <h4 class="mb-0 font-weight-bolder">{{ title }}</h4>
            </div>
            <b-badge pill :variant="variant">{{ totalCount }}</b-badge>
        </div>

        <!-- Body -->
        <div class="category-card__body">

            <!-- Loading skeleton -->
            <template v-if="loading">
                <div v-for="n in 5" :key="`sk-${n}`" class="category-row">
                    <div class="w-100">
                        <div class="skeleton skeleton-line" style="width: 80%"></div>
                        <div class="skeleton skeleton-line skeleton-line--sm" style="width: 40%"></div>
                    </div>
                </div>
            </template>

            <!-- Empty -->
            <div v-else-if="!items.length" class="text-center text-muted py-3">
                <feather-icon icon="InboxIcon" size="28" class="mb-50 d-block mx-auto" />
                <small>Aucun produit pour le moment</small>
            </div>

            <!-- Items -->
            <template v-else>
                <div v-for="(item, i) in items" :key="i" class="category-row">
                    <div class="category-row__info" @click="$emit('details', item)">
                        <span class="category-row__name">{{ item.Description }}</span>
                        <small class="text-muted d-block">{{ formatPrice(item.Unit_Price) }} TND</small>
                    </div>
                    <div class="d-flex align-items-center flex-shrink-0">
                        <b-badge v-if="promo" variant="light-success" class="mr-50">
                            {{ item.Sales_threshold }} → {{ item.Free_Sales_Qty }}
                        </b-badge>
                        <b-badge v-else-if="metricField && item[metricField] != null" :variant="`light-${variant}`"
                            class="mr-50">
                            {{ item[metricField] }}
                        </b-badge>
                        <b-button variant="success" class="btn-icon" size="sm" style="padding: 5px;"
                            v-b-tooltip.hover.top="'Ajouter à la commande'" @click="$emit('add', item)">
                            <feather-icon icon="ShoppingCartIcon" size="15" />
                        </b-button>
                    </div>
                </div>
            </template>
        </div>

        <!-- Footer -->
        <div class="category-card__footer">
            <b-button block size="sm" :variant="`outline-${variant}`" :disabled="loading || !totalCount"
                @click="$emit('show-all')">
                Voir tout ({{ totalCount }})
            </b-button>
        </div>

    </b-card>
</template>

<script>
import { formatPrice } from '@/utils/formatting';

export default {
    name: 'CategoryPreviewCard',
    props: {
        title: { type: String, required: true },
        icon: { type: String, default: 'PackageIcon' },
        variant: { type: String, default: 'primary' },
        items: { type: Array, default: () => [] },
        totalCount: { type: Number, default: 0 },
        loading: { type: Boolean, default: false },
        // Optional right-side metric chip (e.g. 'Open_Quantity' for répartition quota)
        metricField: { type: String, default: '' },
        // Render the promo badge (Sales_threshold -> Free_Sales_Qty) instead of a metric chip
        promo: { type: Boolean, default: false },
    },
    methods: {
        formatPrice,
    },
};
</script>

<style lang="scss" scoped>
.category-card {
    overflow: hidden;
    border: 1px solid rgba(34, 41, 47, 0.06);
    transition: transform .2s ease, box-shadow .2s ease;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 24px rgba(34, 41, 47, 0.12);
    }
}

.category-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.9rem 1.1rem;
}

.category-card__body {
    flex: 1 1 auto;
    padding: 0.25rem 0.5rem;
    min-height: 230px;
}

.category-card__footer {
    padding: 0.6rem 1rem 1rem;
}

.category-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.55rem 0.6rem;
    border-bottom: 1px solid rgba(34, 41, 47, 0.06);

    &:last-child {
        border-bottom: 0;
    }
}

.category-row__info {
    min-width: 0;
    cursor: pointer;
    padding-right: 0.5rem;

    &:hover .category-row__name {
        text-decoration: underline;
    }
}

.category-row__name {
    display: block;
    font-weight: 600;
    font-size: 0.85rem;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

/* lightweight skeleton */
.skeleton {
    background: linear-gradient(90deg, #eef1f4 25%, #f7f8fa 37%, #eef1f4 63%);
    background-size: 400% 100%;
    animation: skeleton-loading 1.4s ease infinite;
    border-radius: 4px;
}

.skeleton-line {
    height: 12px;
    margin: 6px 0;

    &--sm {
        height: 8px;
    }
}

@keyframes skeleton-loading {
    0% {
        background-position: 100% 50%;
    }

    100% {
        background-position: 0 50%;
    }
}
</style>
